import axios, { AxiosRequestConfig } from "axios";
import {
  CourierDTO,
  CourierPackageTypeDTO,
  CourierServiceDTO,
  MbePackTypeDTO,
  MbeServiceDTO,
  OnlineMbeCourierVisibilityDto,
  ShipmentDetailsInputDTO,
  ShipmentDetailsOutputDTO,
  SubcountryDTO,
  PaymentTypesDTO,
  WineCourierServiceDTO
} from "@/model/data.model";
import store from "../vuex/store";
import { getStoreId, getTenantId } from "./AuthService";
import MBEService from "@/components/shipments/EditShipment/MBEService.vue";
import { AxiosRequestConfigDef } from "@/event-bus";
import { WineWarehouseImporterDTO } from "@/model/component.model";

export class PriceListService {
  static getSubcountries(
    countryId: string,
    zipcode = "",
    storeZipCode = "",
    storeIsoCode = ""
  ): Promise<SubcountryDTO[]> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url:
        store.getters.BASE_API_URL +
        "/pricelist/configuration/subcountrybycountry",
      params: {
        countryId: countryId,
        zipcode: zipcode !== "" ? zipcode : null,
        storeZipCode: storeZipCode,
        storeIsoCode: storeIsoCode
      }
    };
    return axios.request(config).then(res => res.data);
  }

  static getAllSubCountries(): Promise<SubcountryDTO[]> {
    return axios
      .get(store.getters.BASE_API_URL + "/pricelist/configuration/subcountries")
      .then(res => res.data as SubcountryDTO[]);
  }

  static getMbeServices(
    subcountryToId = 0,
    cancelSource: any = undefined,
    subcountryFromId = 0
  ): Promise<MbeServiceDTO[]> {
    let params: any =
      cancelSource !== undefined ? { cancelToken: cancelSource.token } : {};
    if (subcountryToId == 0) {
      return store
        .dispatch(
          "cachemod/getMbeServiceForCourierService",
          {
            idBusinessCourierService: null,
            cancelToken: cancelSource != null ? cancelSource : null
          },
          {
            timeout: store.state["cachemod"].timeouts.mbeServices
          }
        )
        .then((services: MbeServiceDTO[]) => {
          return services;
        });
    } else {
      if (!subcountryFromId) {
        subcountryFromId = subcountryToId;
      } else {
        params = { ...params, subcountryTo: subcountryToId };
      }
      return axios
        .get(
          store.getters.BASE_API_URL +
            `/pricelist/configuration/mbe-services/subcountry/${subcountryFromId}`,
          { params }
        )
        .then(res => res.data);
    }
  }

  static getMbeServiceForCourierService(
    courierServiceId?: number,
    cancelToken?: any
  ): Promise<MbeServiceDTO[]> {
    return axios
      .get(
        store.getters.BASE_API_URL + "/pricelist/configuration/mbe-services",
        {
          params: { idBusinessCourierService: courierServiceId },
          cancelToken: cancelToken
        }
      )
      .then(res => res.data);
  }

  static getMbePackTypes(): Promise<MbePackTypeDTO[]> {
    return axios
      .get(
        store.getters.BASE_API_URL + "/pricelist/configuration/mbe-pack-types"
      )
      .then(res => res.data);
  }

  getExtraServices() {
    return axios
      .get(
        store.getters.BASE_API_URL + "/pricelist/configuration/extra-services"
      )
      .then(res => res.data);
  }

  getWineCourierService(courier: string, type: string) {
    const config: AxiosRequestConfig = {
      method: "GET",
      params: {
        courier,
        type,
        tenantId: getTenantId()
      },
      url: store.getters.BASE_API_URL + "/pricelist/wine2/wine-courier-service"
    };

    return axios.request(config).then(res => res.data as WineCourierServiceDTO);
  }

  static async getCouriers(
    subcountryFromId = 0,
    subcountryToId = 0,
    shipmentType = "",
    mbeServiceBusinessId = 0,
    cancelSource: any = undefined
  ): Promise<CourierDTO[]> {
    let getParams: any = {
      storeId: getStoreId()
    };
    if (subcountryFromId != 0) {
      getParams.subFromId = subcountryFromId;
    }
    if (subcountryToId != 0) {
      getParams.subToId = subcountryToId;
    }
    if (shipmentType != "") {
      getParams.shipType = shipmentType;
    }
    if (mbeServiceBusinessId != 0) {
      getParams.idBusinessMBEService = mbeServiceBusinessId;
    }
    if (cancelSource !== undefined) {
      getParams = { ...getParams, cancelToken: cancelSource.token };
    }

    let filteredVis: OnlineMbeCourierVisibilityDto[] = [];
    const visibilities: OnlineMbeCourierVisibilityDto[] =
      store.getters["customer/courierVisibilityConfig"];
    if (shipmentType.trim() !== "") {
      filteredVis = visibilities.filter(
        vis => String(vis.shipmentType) === String(shipmentType)
      );
    } else {
      filteredVis = visibilities;
    }
    if (mbeServiceBusinessId) {
      // filter courier service by mbeService databaseid if any
      const serviceDbId = store.state["shipment"].mbeServices.find(
        value => value.idMBEBusiness === mbeServiceBusinessId
      )?.idMBEService;
      if (serviceDbId) {
        filteredVis = filteredVis.filter(
          value => value.mbeServiceId === serviceDbId
        );
      }
    }

    // deduplicate ids using Set
    getParams["courierServiceIds"] = Array.from(
      new Set(filteredVis.map(value => value.courierServiceId))
    ).join(",");

    if (cancelSource === undefined) {
      // use cached if no need to cancel
      return store.cache.dispatch("cachemod/couriersByServices", getParams, {
        timeout: store.state["cachemod"].timeouts.couriersByServices
      });
    }

    return await axios
      .get(
        store.getters.BASE_API_URL +
          "/pricelist/configuration/couriers-byservices",
        {
          params: getParams
        }
      )
      .then((res: any) => {
        return res.data as CourierDTO[];
      });
  }

  static couriersByServices(
    subFromId?: number,
    subToId?: number,
    shipType?: string,
    idBusinessMBEService?: number,
    courierServiceIds?: number[]
  ): Promise<CourierDTO[]> {
    const config: AxiosRequestConfigDef = {
      method: "GET",
      params: {
        storeId: getStoreId(),
        subFromId: subFromId,
        subToId: subToId,
        shipType: shipType,
        idBusinessMBEService: idBusinessMBEService,
        courierServiceIds: courierServiceIds
      },
      progress: false,
      url:
        store.getters.BASE_API_URL +
        "/pricelist/configuration/couriers-byservices"
    };

    return axios.request(config).then(res => res.data as CourierDTO[]);
  }

  static couriersByIdService(
    courierServiceIds?: string
  ): Promise<CourierDTO[]> {
    const config: AxiosRequestConfig = {
      method: "GET",
      params: {
        storeId: getStoreId(),
        courierServiceIds: courierServiceIds
      },

      url:
        store.getters.BASE_API_URL +
        "/pricelist/configuration/couriers-byservices"
    };

    return axios.request(config).then(res => res.data);
  }

  static getCourierServices(
    idCourier: number,
    subcountryFromId = 0,
    subcountryToId = 0,
    shipmentType = "",
    courierPackageType = 0,
    mbeService = 0,
    cancelSource: any = undefined,
    autopilot = false
  ): Promise<CourierServiceDTO[]> {
    const params: any = { idCourier: idCourier };
    if (subcountryFromId != 0) {
      params.subFromId = subcountryFromId;
    }
    if (subcountryToId != 0) {
      params.subToId = subcountryToId;
    }
    if (shipmentType != "") {
      params.shipType = shipmentType;
    }
    if (mbeService != 0) {
      params.idBusinessMBEService = mbeService;
    }
    if (courierPackageType != 0) {
      params.idBusinessPackType = courierPackageType;
    }

    const config: AxiosRequestConfigDef = {
      method: "GET",
      url:
        store.getters.BASE_API_URL +
        "/pricelist/configuration/courier-services",
      params: params,
      cancelToken: cancelSource ? cancelSource.token : undefined,
      progress: false
    };
    return axios.request(config).then((res: any) => {
      if (autopilot) return res.data;
      //filter by customer restrictions
      const courierServices: CourierServiceDTO[] = res.data;
      const courierServicesFiltered: CourierServiceDTO[] = [];
      const visibilities: OnlineMbeCourierVisibilityDto[] =
        store.getters["customer/courierVisibilityConfig"];
      let filtered: OnlineMbeCourierVisibilityDto[] = [];
      if (shipmentType.trim() !== "") {
        filtered = visibilities.filter(
          vis => String(vis.shipmentType) === String(shipmentType)
        );
      } else {
        filtered = visibilities;
      }
      courierServices.forEach((element: CourierServiceDTO, index) => {
        if (
          filtered.some(
            (vis: OnlineMbeCourierVisibilityDto) =>
              vis.courierServiceId === element.idCourierService
          )
        ) {
          courierServicesFiltered.push(element);
        }
      });
      return courierServicesFiltered;
    });
  }

  static getCourierPackageTypes(
    idCourier: number,
    subcountryFromId = 0,
    subcountryToId = 0,
    shipmentType = "",
    MBEServiceId = 0,
    idMBEBusiness = 0,
    idMBEPackType = 0
  ): Promise<CourierPackageTypeDTO[]> {
    const params: any = { idCourier: idCourier };
    if (subcountryFromId != 0) {
      params.subFromId = subcountryFromId;
    }
    if (subcountryToId != 0) {
      params.subToId = subcountryToId;
    }
    if (shipmentType != "") {
      params.shipType = shipmentType;
    }
    if (MBEServiceId != 0) {
      params.idBusinessMBEService = MBEServiceId;
    }
    if (idMBEBusiness != 0) {
      params.idMBEBusiness = idMBEBusiness; //courier business
    }
    if (idMBEPackType != 0) {
      params.idMBEPackType = idMBEPackType; //business id of packtype mbe
    }
    const config: AxiosRequestConfigDef = {
      method: "GET",
      url:
        store.getters.BASE_API_URL +
        "/pricelist/configuration/courier-pack-types",
      params: params,
      progress: false
    };
    return axios.request(config).then(res => res.data);
  }

  getExtraServicesByCourierId(id: number) {
    return axios
      .get(
        store.getters.BASE_API_URL + "/pricelist/configuration/extra-services",
        {
          params: {
            idCourier: id
          }
        }
      )
      .then(res => res);
  }

  static getShipmentPriceDetails(
    shipment_details: ShipmentDetailsInputDTO,
    autopilot?: boolean
  ): Promise<ShipmentDetailsOutputDTO[]> {
    if (autopilot) {
      shipment_details.autopilotCourierIdBusiness = undefined;
      shipment_details.autopilotCourierName = undefined;
    }
    const config: AxiosRequestConfig = {
      method: "POST",
      params: { autopilot: autopilot ? autopilot : undefined },
      data: [shipment_details],
      url: store.getters.BASE_API_URL + "/pricelist/shipment/details"
    };

    return axios.request(config).then(res => res.data);
  }

  static getPaymentTypes(
    idBusinessCourier: number,
    extraServiceId: number
  ): Promise<PaymentTypesDTO[]> {
    const config: AxiosRequestConfigDef = {
      method: "GET",
      url:
        store.getters.BASE_API_URL +
        "/pricelist/configuration/extra-service/payment-types",
      params: {
        idBusinessCourier: idBusinessCourier,
        extraServiceId: extraServiceId
      },
      progress: false
    };
    return axios.request(config).then(res => res.data);
  }

  static shipmentPriceCalculate(data) {
    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${store.getters.BASE_API_URL}/pricelist/online-mbe/shipment-price`,
      data: data
    };
    return axios.request(config).then(res => res.data);
  }

  static async getAutopilotCouriers(
    shipment_details: ShipmentDetailsInputDTO
  ): Promise<ShipmentDetailsOutputDTO[]> {
    const config: AxiosRequestConfig = {
      method: "POST",
      data: [shipment_details],
      url: store.getters.BASE_API_URL + "/pricelist/shipment/availableCouriers"
    };
    return axios.request(config).then(res => res.data);
  }

  static async getAutopilotMBEServices(
    shipment_details: ShipmentDetailsInputDTO
  ): Promise<number[]> {
    const config: AxiosRequestConfig = {
      method: "POST",
      data: [shipment_details],
      url:
        store.getters.BASE_API_URL +
        "/pricelist/shipment/autopilotAvailableMbeServices"
    };
    return axios.request(config).then(res => res.data);
  }

  static async getFuelPrices(): Promise<any> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/pricelist/extraservice-fuel-price"
    };
    return axios.request(config).then(res => res.data);
  }
}
